import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import config from "@config"
import css from "./Header.scss"
import GAEvents from "./GaEvents"
import Search from "@containers/Search"
import SubHeader from "@containers/SubHeader/SubHeader"
import { getCart, handleAuthentication, setIsLogin, getLabsCart } from "@containers/App/reducer"
import CategoryTree from "@containers/CategoryTree"
import { isDwebUrl, isLabsPwaUrl } from "@lib/urlMatchUtility"
import Separator from "@components/Separator/Separator"
import AnimateTag from "@components/AnimateTag/AnimateTag"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import { getCookie, setCookie, getAssetPath, getDecodedAffiliateCookie } from "@commonUtils"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { updateCurrentCity, getCurrentCityDetails } from "@containers/LocationPicker/reducer"
import { getSessionAndLogin, updateTdlSsoAuthSession } from "../../lib/tdlSsoAuth"
import { sendMixpanelTrackEvent } from "@lib/gtmUtility"

const oneMgLogo = getAssetPath("svgs/icons/1mg_logo_header.svg")

const cartUrlsMap = {
    pharmacy: "/cart",
    labs: "/labs/cart",
}

const isCarePlanLink = (link = "") => {
    return link.includes("/subscription-plan")
}

const Logo = ({ corporateDetails }) => {
    const { cartType, headerLogoUrl, pageName } = useSelector((state) => state.shellReducer)
    const redirectLink = cartType === "labs" ? "/labs" : "/"
    const handleLogoClick = () => {
        sendMixpanelTrackEvent("top_navigation_clicked", {
            position: 0,
            category_name: "logo_click",
            category_type: "top_nav",
            target_url: redirectLink,
            page_name: pageName,
        })
    }
    return (
        <ConditionalLink
            onClick={handleLogoClick}
            to={redirectLink}
            isExternal={!isDwebUrl("/")}
            className="flex justifyCenter alignCenter"
        >
            <ImageLoader
                alt="Tata 1mg logo"
                height="36px"
                width="124px"
                src={headerLogoUrl || corporateDetails?._logo_url || oneMgLogo}
                className={corporateDetails?._logo_url || headerLogoUrl ? css.corpLogo : css.logo}
                loadingClassName={css.logoFallback}
            />
        </ConditionalLink>
    )
}

const CartPopup = ({ totalQty, itemsObject, onCartClick }) => {
    const items = Object.keys(itemsObject)
    return (
        <div className={`padding-8-0 ${css.cartPopup}`}>
            <div className="bodySemiBold col-4 flex justifyBetween">
                <div>Order Summary</div>
                <div>
                    {totalQty} {totalQty > 1 ? "Items" : "Item"}
                </div>
            </div>
            <Separator size="xs" marginTop={4} marginBottom={4} />
            <div className="col-4">
                {items.length <= 2 &&
                    items?.slice(0, 2).map((key) => {
                        return (
                            <div key={key} className="flex justifyBetween padding-8-0">
                                <div>{itemsObject[key]?.name}</div>
                                <div className="textSecondary xSmallMedium">
                                    Qty: {itemsObject[key]?.quantity}
                                </div>
                            </div>
                        )
                    })}
            </div>
            {items.length > 2 && (
                <ConditionalLink
                    to="/cart"
                    className="noAnchorColor pointer"
                    onClick={onCartClick}
                    isExternal={!isDwebUrl("/cart")}
                >
                    <div className="col-4 bodySemiBold textSecondary">
                        +{items.length - 2} More {items.length - 2 > 1 ? "Items" : "Item"}
                    </div>
                </ConditionalLink>
            )}
            <ConditionalLink
                to={"/cart"}
                isExternal={!isDwebUrl("/cart")}
                className={`flex justifyCenter bodySemiBold textAction padding-8-0 pointer`}
                onClick={() => {
                    GAEvents.onTopNavClick("proceedToCart")
                    onCartClick({ text: "PROCEED TO CART", to: "/cart" })
                }}
            >
                PROCEED TO CART
            </ConditionalLink>
        </div>
    )
}

const CartPopupLabs = ({ totalQty, itemsObject, onCartClick }) => {
    const items = Object.keys(itemsObject)
    const [showHiddenTests, setShowHiddenTests] = useState(false)
    const DEFAULT_LIMIT = 2
    const currentLimit = showHiddenTests ? items.length : DEFAULT_LIMIT
    return (
        <div className={`padding-8-0 ${css.cartPopup}`}>
            <div className="bodySemiBold col-4 flex justifyBetween">
                <div>Order Summary</div>
                <div>
                    {totalQty} {totalQty > 1 ? "Test(s)" : "Test"}
                </div>
            </div>
            <Separator size="xs" marginTop={4} marginBottom={4} />
            <div className="col-4">
                {items?.slice(0, currentLimit).map((key) => {
                    return (
                        <div key={key} className="flex justifyBetween padding-8-0">
                            <div>{itemsObject[key]?.header}</div>
                        </div>
                    )
                })}
            </div>
            {items.length > currentLimit && (
                <div
                    className="col-4 bodySemiBold textSecondary pointer"
                    onClick={() => setShowHiddenTests(true)}
                >
                    +{items.length - currentLimit} More {items.length - currentLimit > 1 ? "Tests" : "Test"}
                </div>
            )}
            <ConditionalLink
                to={"/labs/cart"}
                isExternal={!isDwebUrl("/labs/cart")}
                className={`flex justifyCenter bodySemiBold textAction padding-8-0 pointer`}
                onClick={() => {
                    GAEvents.onTopNavClick("proceedToCart")
                    onCartClick()
                }}
            >
                PROCEED TO CART
            </ConditionalLink>
        </div>
    )
}

const AuthLinks = ({ onTopNavClick }) => {
    const dispatch = useDispatch()
    return (
        <div className="flex">
            <ConditionalLink
                className="noAnchorColor"
                onClick={() => {
                    dispatch(handleAuthentication())
                    dispatch(setIsLogin(true))
                    onTopNavClick({ text: "Login" })
                }}
            >
                <div className={`bodySemiBold ${css.cursorAuthLinks}`}>Login</div>
            </ConditionalLink>
            <div className="gutter-only">|</div>
            <ConditionalLink
                onClick={() => {
                    dispatch(handleAuthentication())
                    dispatch(setIsLogin(false))
                    onTopNavClick({ text: "Signup" })
                }}
            >
                <div className={`bodySemiBold ${css.cursorAuthLinks}`}>Signup</div>
            </ConditionalLink>
        </div>
    )
}

const UserDropdown = ({ data, onTopNavClick }) => {
    const csrfToken = useRef(null)
    const location = useLocation()
    const { userData, carePlanData, loggedIn } = useSelector((state) => state.shellReducer)

    useEffect(() => {
        csrfToken.current = window.document.getElementsByName("_csrf")?.[0]?.value || ""
    }, [])

    const carePlanItem = {
        url: carePlanData?.redirect_url || "/subscription-plan/subscriptions",
        text: carePlanData?.text || "CARE PLAN",
        // tag is being disabled for care plan links
        tag: carePlanData &&
            !isCarePlanLink(location.pathname) &&
            (carePlanData.renew || !carePlanData.expiry) && {
                text: carePlanData.renew ? "RENEW NOW" : "SAVE MORE",
            },
    }

    const corporateDetails = getCookie("corporateDetails")

    const corporateItem = {
        url: corporateDetails?._redirect_url,
        text: "My Corporate Dashboard",
        tag: {
            text: "NEW",
        },
    }

    return (
        <div className={`bodySemiBold paddingBoth-16 ${css.dropdownLinks}`}>
            <div className="padding-0-16 l5SemiBold textSecondary marginTop-24">Hi, there</div>
            <Separator size="xs" marginTop={6} />
            <div className="padding-0-16">
                {data.map((item) => (
                    <div key={item.to} className="marginTop-16">
                        <ConditionalLink
                            onClick={() => {
                                onTopNavClick(item)
                            }}
                            key={item.to}
                            to={item.to}
                            isExternal={!isDwebUrl(item.to)}
                            className="flex alignCenter textSecondary"
                        >
                            <div>{item.text}</div>
                            {item.showTag && (
                                <div className={css.marginLeft8}>
                                    <AnimateTag text={item.tagText} size="md" />
                                </div>
                            )}
                        </ConditionalLink>
                    </div>
                ))}
            </div>
            <ConditionalLink
                to={carePlanItem.url}
                isExternal={!isDwebUrl(carePlanItem.url)}
                className="marginTop-16 padding-0-16 flex alignCenter textSecondary"
            >
                <div>Care Plan</div>
                {carePlanItem.tag && (
                    <div className={css.marginLeft8}>
                        <AnimateTag text={carePlanItem.tag.text} size="md" />
                    </div>
                )}
            </ConditionalLink>
            {userData?.IS_LOYAL_USER ? (
                <ConditionalLink
                    to="/loyalty"
                    isExternal={!isDwebUrl("/loyalty")}
                    className="marginTop-16 padding-0-16 flex justifyBetween textSecondary"
                >
                    <div>NeuCoins</div>
                    {userData?.tcpPoints >= 0 && <div className="textAction">{userData.tcpPoints}</div>}
                </ConditionalLink>
            ) : (
                <ConditionalLink
                    to="/1mgcash/dashboard"
                    isExternal={!isDwebUrl("/1mgcash/dashboard")}
                    className="marginTop-16 padding-0-16 flex justifyBetween textSecondary"
                >
                    <div>1mg Cash</div>
                    {userData?.cashBalance >= 0 && <div className="textAction">₹{userData.cashBalance}</div>}
                </ConditionalLink>
            )}
            {corporateDetails && corporateDetails?._redirect_url && (
                <ConditionalLink
                    to={corporateItem.url}
                    isExternal={!isDwebUrl(corporateItem.url)}
                    className="marginTop-16 padding-0-16 flex alignCenter textSecondary"
                >
                    <div>{corporateItem.text}</div>
                    {corporateItem.tag && (
                        <div className={css.marginLeft8}>
                            <AnimateTag text={corporateItem.tag.text} size="md" />
                        </div>
                    )}
                </ConditionalLink>
            )}
            {/*
                Block below carters the need for super login/logout functionality, only admin and telesales users have this
                access

                Super login block - Start
            */}
            {userData?.showSuperLogin && (
                <ConditionalLink
                    to="/hkpadmin/superlogin"
                    isExternal={!isDwebUrl("/hkpadmin/superlogin")}
                    className="textSecondary"
                >
                    <div className={`padding-0-16 marginBoth-16`}>Super Login</div>
                </ConditionalLink>
            )}
            {userData?.showSuperLogout && (
                <ConditionalLink
                    to="/hkadmin/logout"
                    className="textSecondary"
                    isExternal={!isDwebUrl("/hkadmin/logout")}
                >
                    <div className={`padding-0-16 marginBoth-16`}>Super Logout</div>
                </ConditionalLink>
            )}

            {loggedIn &&
                getDecodedAffiliateCookie("affiliateDetails") !== "tcp" &&
                getDecodedAffiliateCookie("affiliateDetails") !== "bigbasket" && (
                    <ConditionalLink
                        to={`/logout?_csrf=${encodeURIComponent(csrfToken.current)}`}
                        isExternal={!isDwebUrl("/logout")}
                        className="textSecondary"
                    >
                        <div className={`padding-0-16 marginBoth-16`}>Logout</div>
                    </ConditionalLink>
                )}
        </div>
    )
}

const Navigation = ({ data, onTopNavClick }) => {
    const location = useLocation()
    const { carePlanData } = useSelector((state) => state.shellReducer)

    const carePlanItem = {
        to: carePlanData?.redirect_url || "/subscription-plan/subscriptions",
        text: carePlanData?.text || "CARE PLAN",
        // tag is being disabled for care plan links
        tag: carePlanData &&
            !isCarePlanLink(location.pathname) &&
            (carePlanData.renew || !carePlanData.expiry) && {
                text: carePlanData.renew ? "RENEW NOW" : "SAVE MORE",
                animate: false,
            },
    }

    return (
        <div className="flex">
            {data.map((item, index) => (
                <ConditionalLink
                    onClick={() => onTopNavClick(item, index)}
                    key={item.to}
                    to={item.to}
                    isExternal={!isDwebUrl(item.to)}
                    className={`noAnchorColor flex alignCenter ${
                        location.pathname === item.to ? "textAction" : css.navigationItem
                    }`}
                >
                    <div className="l5SemiBold padding-0-8">{item.text}</div>
                    {item.tag && <AnimateTag text={item.tag.text} size="md" animate={item.tag.animate} />}
                </ConditionalLink>
            ))}
            <ConditionalLink
                onClick={() => onTopNavClick(carePlanItem, data?.length)}
                to={carePlanItem.to}
                isExternal={!isDwebUrl(carePlanItem.url)}
                className={`noAnchorColor flex alignCenter ${
                    isCarePlanLink(location.pathname) ? "textAction" : css.navigationItem
                }`}
            >
                <div className="l5SemiBold padding-0-8">{carePlanItem.text}</div>
                {carePlanItem.tag && (
                    <AnimateTag text={carePlanItem.tag.text} size="md" animate={carePlanItem.tag.animate} />
                )}
            </ConditionalLink>
        </div>
    )
}

const Header = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const {
        loggedIn,
        cartCount,
        labsCartCount,
        itemsInCart,
        itemsInLabsCart,
        showSubHeader,
        showCategoryTree,
        showNavigationLinks,
        showSearch,
        showProfile,
        showOffers,
        showCart,
        showHelp,
        showCorporateDashboardLink,
        cartType,
        corporateDetails,
        pageName,
    } = useSelector((state) => state.shellReducer)
    const { currentCity } = useSelector((state) => state.locationsReducer)

    const isLabs = cartType === "labs" ? true : false
    const currentCartCount = isLabs ? labsCartCount : cartCount
    const currentItemsinCart = isLabs ? itemsInLabsCart : itemsInCart

    useEffect(() => {
        if (isLabsPwaUrl(location.pathname)) {
            dispatch(getLabsCart())
        } else {
            dispatch(getCart())
        }
    }, [location])

    const corporateItem = {
        url: corporateDetails?._redirect_url,
        text: "My Corporate Dashboard",
    }

    useEffect(() => {
        const city = config.defaultCity
        const cookieCity = getCookie("city")

        if (!cookieCity) {
            setCookie("city", city)
            dispatch(getCurrentCityDetails(city))
        }

        if ((cookieCity && currentCity !== cookieCity) || currentCity === null) {
            dispatch(updateCurrentCity(cookieCity))
            dispatch(getCurrentCityDetails(cookieCity))
        }
    }, [currentCity])

    useEffect(() => {
        const tdlSsoAction = getCookie("tdlSsoAction")
        if (tdlSsoAction) {
            dispatch(updateTdlSsoAuthSession(tdlSsoAction))
        } else if (!getCookie("loggedIn")) {
            dispatch(getSessionAndLogin())
        }
    }, [])

    // TODO: move these to a config or find a better way
    const navigationLinks = [
        {
            to: "/",
            text: "MEDICINES",
        },
        {
            to: "/labs",
            text: "LAB TESTS",
            tag: {
                text: "SAFE",
                animate: true,
            },
        },
        {
            to: "/online-doctor-consultation",
            text: "CONSULT DOCTORS",
        },
        {
            to: "/coronavirus-covid19",
            text: "COVID-19",
        },
        {
            to: "/partnerships",
            text: "PARTNERSHIPS",
            showTag: true,
            tagText: "NEW",
        },
    ]

    const dropdownLinks = [
        {
            to: "/profile",
            text: "View Profile",
        },
        {
            to: "/order/history",
            text: "My Orders",
        },
        {
            to: "/reorder",
            text: "Previously Ordered Items",
            showTag: true,
            tagText: "NEW",
        },
        {
            to: "/review-rating",
            text: "Rate your Recent Purchases",
            showTag: true,
            tagText: "NEW",
        },
        {
            to: "/labs/mybookings",
            text: "My Lab Tests",
        },
        {
            to: "/online-doctor-consultation/myconsultation",
            text: "My Consultations",
        },
        {
            to: "/health-record",
            text: "My Health Records",
            showTag: true,
            tagText: "NEW",
        },
        {
            to: "/dashboard/manage-payment",
            text: "Manage Payments",
        },
    ]

    const onTopNavClick = (data, index = null) => {
        sendMixpanelTrackEvent("top_navigation_clicked", {
            page_name: pageName,
            category_name: data.text,
            category_type: "top_nav",
            target_url: data.to,
            ...(index !== null && { position: index + 1 }),
        })
    }
    const onCartClick = () => {
        sendMixpanelTrackEvent("go_to_cart_clicked", {
            page_name: pageName,
            cta_action: "go_to_cart",
            cta_type: "icon",
        })
    }

    return (
        <div className={css.header}>
            <div>
                <div className="flex justifyBetween alignCenter padding-0-16 padding-8-0">
                    <div className="col-18 flex alignCenter">
                        <Logo corporateDetails={corporateDetails} />
                        {/*The navigation links are shown and hidden as a whole for now, can be changed to individual elements later if needed */}
                        {showNavigationLinks && (
                            <Navigation data={navigationLinks} onTopNavClick={onTopNavClick} />
                        )}
                        {showSearch && <Search />}
                    </div>
                    {(showProfile || showOffers || showCart || showHelp || showCorporateDashboardLink) && (
                        <div className="col-8 flex alignCenter justifyEnd">
                            {showProfile && (
                                <div className={css.marginLeft16}>
                                    {loggedIn ? (
                                        <div className={`${css.profileContainer}`}>
                                            <ImageLoader
                                                width="22px"
                                                height="22px"
                                                src="https://onemg.gumlet.io/profile-rebrand_aboy0q.svg"
                                            />
                                            <UserDropdown
                                                onTopNavClick={onTopNavClick}
                                                data={dropdownLinks}
                                            />
                                        </div>
                                    ) : (
                                        <AuthLinks onTopNavClick={onTopNavClick} />
                                    )}
                                </div>
                            )}
                            {showOffers && (
                                <ConditionalLink
                                    to="/offers"
                                    className={`${css.marginLeft16} noAnchorColor`}
                                    isExternal={!isDwebUrl("/offers")}
                                    onClick={() => {
                                        GAEvents.onTopNavClick("Offers")
                                        onTopNavClick({ text: "Offers", to: "/offers" })
                                    }}
                                >
                                    <div className="bodyRegular">Offers</div>
                                </ConditionalLink>
                            )}
                            {showCart && (
                                <>
                                    <ConditionalLink
                                        onClick={onCartClick}
                                        to={cartUrlsMap[cartType]}
                                        isExternal={!isDwebUrl(cartUrlsMap[cartType])}
                                        className={`${css.marginLeft16} noAnchorColor ${css.cartContainer}`}
                                    >
                                        {currentCartCount > 0 && (
                                            <div className={css.quantityPopup}>
                                                <AnimateTag size="sm" text={currentCartCount} />
                                            </div>
                                        )}
                                        <ImageLoader
                                            width="22px"
                                            height="22px"
                                            src="https://onemg.gumlet.io/cart-icon-rebrand_vp4k0f.svg"
                                        />
                                    </ConditionalLink>
                                    {isLabs ? (
                                        <CartPopupLabs
                                            totalQty={currentCartCount}
                                            itemsObject={currentItemsinCart}
                                            onTopNavClick={onCartClick}
                                        />
                                    ) : (
                                        <CartPopup
                                            totalQty={currentCartCount}
                                            itemsObject={currentItemsinCart}
                                            onTopNavClick={onCartClick}
                                        />
                                    )}
                                </>
                            )}
                            {showCorporateDashboardLink && loggedIn && corporateItem.url && (
                                <div className={`${css.profileContainer}`}>
                                    <ConditionalLink
                                        to={corporateItem.url}
                                        isExternal={!isDwebUrl(corporateItem.url)}
                                        className="textSecondary"
                                    >
                                        <p className="l5SemiBold padding-0-8">{corporateItem.text}</p>
                                    </ConditionalLink>
                                </div>
                            )}
                            {showHelp && (
                                <ConditionalLink
                                    to="/help"
                                    className={`${css.marginLeft16} noAnchorColor`}
                                    isExternal={!isDwebUrl("/help")}
                                    onClick={() => {
                                        GAEvents.onTopNavClick("Need Help?")
                                        onTopNavClick({ text: "Need Help?", to: "/help" })
                                    }}
                                >
                                    <div className="bodyRegular">Need Help?</div>
                                </ConditionalLink>
                            )}
                        </div>
                    )}
                </div>
                <Separator size="xs" />
            </div>
            {showSubHeader && (
                <>
                    <SubHeader />
                    <Separator size="xs" />
                </>
            )}
            {showCategoryTree && (
                <>
                    <CategoryTree />
                    <Separator size="xs" />
                </>
            )}
        </div>
    )
}

Header.serverSideFunction = () => {
    return Promise.resolve()
}

export default Header
